import {
  CONTRACT_TYPE_LABELS,
  DO_CONTRACT_TYPES,
} from "@etudescarre/constants";
import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import {
  DocumentTable,
  type DocumentTableProps,
} from "../components/document-table/document-table";
import type { DocumentData, LotData } from "../components/document-table/types";
import { useDocumentActions } from "../components/document-table/use-document-actions";
import { AvancementEtudesContrat } from "./avancement-etudes-contrat";
import { AvancementEtudesDo } from "./avancement-etudes-do";

type ZoneSummary = {
  id: number;
  name: string;
  shortName: string | null;
  type: "horizontale" | "verticale";
  interventionCount: number;
  withoutDocumentCount: number;
  producedDocumentCount: number;
  latestBpeDocumentCount: number;
  forecastDocumentCount: number;
};
type BuildingSummary = {
  id: number;
  name: string;
  shortName: string | null;
  zones: ZoneSummary[];
};
type FilterOption = { id: number; name: string };
type SummaryData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  buildings: BuildingSummary[];
};
type ProviderContract = {
  id: number;
  label: string;
  companyName: string;
  type: "travaux_n_1" | "travaux_n_2";
};
type StudyIntervention = {
  id: number;
  dateIntervention: string | null;
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  providerContracts: ProviderContract[];
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: SummaryData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; zoneId: number; interventions: StudyIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

const doContractTypes: readonly string[] = DO_CONTRACT_TYPES;

const PROVIDER_RANK_LABELS: Record<ProviderContract["type"], string> = {
  travaux_n_1: CONTRACT_TYPE_LABELS.travaux_n_1,
  travaux_n_2: CONTRACT_TYPE_LABELS.travaux_n_2,
};

const interventionLots = (item: StudyIntervention): LotData[] => [
  {
    id: item.lot.id,
    name: item.lot.name,
    nLot: item.lot.nLot,
    prestas: [
      {
        id: item.performance.id,
        name: item.performance.name,
        documents: item.documents,
      },
    ],
  },
];

/**
 * Aiguillage par type de contrat de la société de l'utilisateur :
 * - Travaux rang 1 → vue pilote (écran 012) ;
 * - Travaux rang 2 uniquement → vue "mon contrat" (écran 034) ;
 * - Aucun contrat travaux mais contrat donneur d'ordre (AMO / CT / SPS / MOE)
 *   → vue donneur d'ordre (écran 025).
 */
export function AvancementEtudes() {
  const { contracts } = useYardContext();
  const hasRankOne = contracts.some(
    (contractItem) => contractItem.type === "travaux_n_1",
  );
  const hasRankTwo = contracts.some(
    (contractItem) => contractItem.type === "travaux_n_2",
  );
  const hasDoContract = contracts.some((contractItem) =>
    doContractTypes.includes(contractItem.type),
  );
  if (!hasRankOne && hasRankTwo) return <AvancementEtudesContrat />;
  if (!hasRankOne && !hasRankTwo && hasDoContract)
    return <AvancementEtudesDo />;
  return <AvancementEtudesPilote />;
}

function AvancementEtudesPilote() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    void summaryRefresh;
    if (isNil(yardId)) return;
    let cancelled = false;
    // Keep the screen in place while it reloads after an action.
    setState((current) =>
      current.status === "success" ? current : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedBuildingId(
          (current) => current ?? data.data.buildings[0]?.id ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery, summaryRefresh]);

  useEffect(() => {
    void detailRefresh;
    if (isNil(yardId) || isNil(selectedZoneId)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail((current) =>
      current.status === "success" && current.zoneId === selectedZoneId
        ? current
        : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/zones/{zoneId}", {
        params: {
          path: { yardId, zoneId: String(selectedZoneId) },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          zoneId: selectedZoneId,
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    selectedZoneId,
    lotQuery,
    performanceQuery,
    recentOnly,
    detailRefresh,
  ]);

  // A success for another zone is still on screen until the effect above
  // runs for the newly selected zone.
  const zoneDetail: DetailState =
    detail.status === "success" && detail.zoneId !== selectedZoneId
      ? { status: "loading" }
      : detail;
  const { tableProps, overlays } = useDocumentActions({
    yardId,
    lots:
      zoneDetail.status === "success"
        ? zoneDetail.interventions.flatMap(interventionLots)
        : [],
    onChanged: () => {
      setSummaryRefresh((value) => value + 1);
      setDetailRefresh((value) => value + 1);
    },
  });

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };

  if (state.status === "loading")
    return <PageMessage text="Chargement de l'avancement des études…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger l'avancement des études."
        error
      />
    );
  if (state.data.buildings.length === 0)
    return (
      <PageMessage text="Aucun bâtiment n'est configuré pour ce chantier." />
    );

  const selectedBuilding =
    state.data.buildings.find((item) => item.id === selectedBuildingId) ??
    state.data.buildings[0];
  const selectedZone =
    selectedBuilding?.zones.find((item) => item.id === selectedZoneId) ?? null;

  return (
    <div className="flex h-full flex-col gap-5 overflow-hidden p-4 lg:p-6">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Avancement des études
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez les interventions que vous pilotez par bâtiment et par
          zone.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto lg:grid-cols-[240px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
        <nav aria-label="Bâtiments" className="space-y-1 lg:overflow-y-auto">
          {state.data.buildings.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setSelectedBuildingId(item.id);
                setSelectedZoneId(null);
              }}
              className={cn(
                "w-full rounded-lg px-3 py-2 text-left text-sm",
                item.id === selectedBuilding?.id
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
              )}
            >
              {item.shortName ?? item.name}
            </button>
          ))}
        </nav>
        <main className="min-w-0 space-y-3 lg:overflow-y-auto">
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
            {selectedBuilding?.name}
          </h3>
          {selectedBuilding?.zones.length === 0 && (
            <p className="text-sm text-zinc-500">
              Aucune zone dans ce bâtiment.
            </p>
          )}
          {selectedBuilding?.zones.map((zoneItem) => (
            <ZoneCard
              key={zoneItem.id}
              zone={zoneItem}
              open={zoneItem.id === selectedZoneId}
              onToggle={() =>
                setSelectedZoneId(
                  zoneItem.id === selectedZoneId ? null : zoneItem.id,
                )
              }
            />
          ))}
          {isNotNil(selectedZone) && (
            <ZoneDetail
              state={zoneDetail}
              yardId={yardId ?? ""}
              tableProps={tableProps}
              onDateSaved={() => setDetailRefresh((value) => value + 1)}
            />
          )}
        </main>
      </div>
      {overlays}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}
function ZoneCard({
  zone,
  open,
  onToggle,
}: {
  zone: ZoneSummary;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">
          {zone.name}
        </span>
        {open ? <CaretDownIcon /> : <CaretRightIcon />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-600 md:grid-cols-5 dark:text-zinc-400">
        <span>{zone.interventionCount} intervention(s)</span>
        <span>{zone.withoutDocumentCount} sans document</span>
        <span>{zone.latestBpeDocumentCount} document(s) BPE</span>
        <span>{zone.producedDocumentCount} document(s) produit(s)</span>
        <span>{zone.forecastDocumentCount} document(s) prévisionnel(s)</span>
      </div>
    </button>
  );
}

type ZoneDetailProps = {
  state: DetailState;
  yardId: string;
  tableProps: Omit<DocumentTableProps, "lots">;
  onDateSaved: () => void;
};
function ZoneDetail({
  state,
  yardId,
  tableProps,
  onDateSaved,
}: ZoneDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette zone."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => (
        <article
          key={item.id}
          className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
                {item.lot.nLot ? `Lot ${item.lot.nLot} — ` : ""}
                {item.performance.name}
              </h4>
              {item.providerContracts.length === 0 ? (
                <p className="mt-1 text-sm text-zinc-500">
                  Aucun contrat prestataire actif
                </p>
              ) : (
                item.providerContracts.map((provider) => (
                  <p key={provider.id} className="mt-1 text-sm text-zinc-500">
                    {`${PROVIDER_RANK_LABELS[provider.type]} : ${provider.label} (${provider.companyName})`}
                  </p>
                ))
              )}
            </div>
            <InterventionDateField
              yardId={yardId}
              interventionId={item.id}
              value={item.dateIntervention}
              onSaved={onDateSaved}
            />
          </div>
          <div className="mt-4">
            {item.documents.length === 0 ? (
              <p className="rounded-lg bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                Aucun document produit pour cette intervention.
              </p>
            ) : (
              <DocumentTable lots={interventionLots(item)} {...tableProps} />
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function InterventionDateField({
  yardId,
  interventionId,
  value,
  onSaved,
}: {
  yardId: string;
  interventionId: number;
  value: string | null;
  onSaved: () => void;
}) {
  const [currentValue, setCurrentValue] = useState(value ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setCurrentValue(value ?? ""), [value]);

  const save = async (dateIntervention: string) => {
    setCurrentValue(dateIntervention);
    setIsSaving(true);
    try {
      const { error } = await apiClient.PATCH(
        "/api/yards/{yardId}/study-progress/interventions/{interventionId}",
        {
          params: { path: { yardId, interventionId: String(interventionId) } },
          body: { dateIntervention: dateIntervention || null },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Date d'intervention non enregistrée",
          description: error.error,
        });
        setCurrentValue(value ?? "");
        return;
      }
      onSaved();
    } catch (err) {
      console.error("Failed to save intervention date", err);
      captureException(err);
      toaster.error({
        title: "Date d'intervention non enregistrée",
        description: "Le serveur n'a pas pu être joint. Réessayez.",
      });
      setCurrentValue(value ?? "");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <label className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
      Date d'intervention
      <input
        type="date"
        value={currentValue}
        disabled={isSaving}
        onChange={(event) => void save(event.target.value)}
        className="mt-1 block rounded border border-zinc-300 bg-white px-2 py-1 text-sm disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800"
      />
      {isSaving && <span className="mt-1 block">Enregistrement…</span>}
    </label>
  );
}
